import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, Languages, FileCheck, Globe, Lock } from 'lucide-react';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ICON_MAP = { Shield, Zap, Languages, FileCheck, Globe, Lock };

export default function Landing() {
  const { t, dir } = useLang();
  const { isAuthenticated } = useAuth();
  const L = t.landing;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--primary)' }}>
      <Header />

      {/* HERO */}
      <section style={{
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #1a1a2e 100%)',
        padding: '100px 24px 80px',
        textAlign: 'center',
        direction: dir,
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(233,69,96,0.12)',
            border: '1px solid rgba(233,69,96,0.3)',
            borderRadius: '20px',
            padding: '6px 18px',
            fontSize: '13px',
            color: '#e94560',
            marginBottom: '24px',
            fontWeight: 600,
          }}>
            AR • FA • EN — v3.0
          </div>

          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 52px)',
            fontWeight: 800,
            lineHeight: 1.25,
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #e94560, #f0a500, #00d4aa)',
            backgroundSize: '200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            {L.hero.title}
          </h1>

          <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.72)', marginBottom: '40px', lineHeight: 1.7 }}>
            {L.hero.subtitle}
          </p>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* SUBSCRIPTIONS_DISABLED: was `to={isAuthenticated ? '/app' : '/register'}` */}
            <Link to="/app" style={{
              display: 'inline-block',
              padding: '16px 40px',
              background: 'linear-gradient(135deg, #e94560, #c0392b)',
              color: 'white',
              borderRadius: '12px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '17px',
              boxShadow: '0 4px 20px rgba(233,69,96,0.5)',
              transition: 'all 0.25s ease',
            }}>
              {L.hero.cta}
            </Link>
            <Link to="/app" style={{
              display: 'inline-block',
              padding: '16px 40px',
              background: 'rgba(255,255,255,0.06)',
              color: '#f0f0f5',
              borderRadius: '12px',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '17px',
              border: '1px solid rgba(255,255,255,0.12)',
            }}>
              {t.nav.app} →
            </Link>
          </div>
          {/* SUBSCRIPTIONS_DISABLED: re-show ctaSub when free-tier limits are active */}
          {false && <p style={{ color: 'rgba(255,255,255,0.52)', fontSize: '13px', marginTop: '14px' }}>{L.hero.ctaSub}</p>}
        </div>
      </section>

      {/* STATS */}
      <section style={{ padding: '52px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', direction: dir }}>
        <div style={{
          maxWidth: '960px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '32px',
          textAlign: 'center',
        }}>
          {L.stats.map((s, i) => {
            const colors = ['#e94560', '#f0a500', '#00d4aa', '#8b5cf6'];
            const c = colors[i % colors.length];
            return (
              <div key={i} style={{
                padding: '20px 16px',
                background: `${c}0a`,
                border: `1px solid ${c}22`,
                borderRadius: '14px',
              }}>
                <div style={{
                  fontSize: '44px', fontWeight: 800, color: c,
                  lineHeight: 1.1, marginBottom: '8px',
                  textShadow: `0 0 20px ${c}60`,
                }}>
                  {s.value}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px', fontWeight: 600 }}>{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ padding: '80px 24px', direction: dir }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 800, marginBottom: '48px', color: '#f0f0f5' }}>
            {L.features.title}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
          }}>
            {L.features.items.map((f, i) => {
              const Icon = ICON_MAP[f.icon] || Shield;
              const colors = ['#e94560','#f0a500','#8b5cf6','#00d4aa','#4e9af1','#06b6d4'];
              const c = colors[i % colors.length];
              return (
                <div key={i} style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '16px',
                  padding: '28px',
                }}>
                  <div style={{
                    width: 48, height: 48,
                    background: `${c}20`,
                    borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '16px',
                  }}>
                    <Icon size={22} color={c} />
                  </div>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#f0f0f5', marginBottom: '8px' }}>{f.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: '80px 24px', background: 'var(--surface)', direction: dir }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 800, marginBottom: '48px', color: '#f0f0f5' }}>
            {L.howItWorks.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '32px' }}>
            {L.howItWorks.steps.map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 56, height: 56,
                  background: 'linear-gradient(135deg, #e94560, #c0392b)',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '20px', fontWeight: 800, color: 'white',
                }}>
                  {s.n}
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#f0f0f5', marginBottom: '8px' }}>{s.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', lineHeight: 1.7 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 2 */}
      <section style={{
        padding: '80px 24px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #1a1a2e, #0f0c29)',
        direction: dir,
      }}>
        <h2 style={{ fontSize: '32px', fontWeight: 800, color: '#f0f0f5', marginBottom: '12px' }}>{L.cta2.title}</h2>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px', marginBottom: '32px' }}>{L.cta2.subtitle}</p>
        {/* SUBSCRIPTIONS_DISABLED: was `to={isAuthenticated ? '/app' : '/register'}` */}
        <Link to="/app" style={{
          display: 'inline-block',
          padding: '16px 48px',
          background: 'linear-gradient(135deg, #e94560, #c0392b)',
          color: 'white',
          borderRadius: '12px',
          textDecoration: 'none',
          fontWeight: 700,
          fontSize: '17px',
          boxShadow: '0 4px 20px rgba(233,69,96,0.5)',
        }}>
          {L.cta2.button}
        </Link>
      </section>

      <Footer />
    </div>
  );
}
